import TripExport from '@koob/trip-export';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import KoobContainer from '@koob/ui/src/components/KoobContainer';
import KoobEmptyState from '@koob/ui/src/components/KoobEmptyState';
import KoobLoaderFullscreen from '@koob/ui/src/components/KoobLoaderFullscreen';
import { getSetting, getSettingLabel } from '@koob/utils';
import {
  getSharedTravelDiary,
  getSharedTravelDiarySettings,
  getSharedHotel,
  getSharedAccommodationLabels,
  getSharedTravelDiaryContents,
  getSharedPricingSummary,
  getSharedOrganizationTheme,
} from '../../api/travelDiary';
import { getExperience } from '../../api/experiences';
import { setPublicShareToken } from '../../api/shareContext';
import { decodeId } from '../../utils';
import { AdminBookingStateEnum } from '@koob/enums';

const noop = () => {};

// Public travel diary (KD-3503): renders the same diary as the authenticated
// export page, but everything is fetched through the unauthenticated
// /travel-diaries/:shareToken endpoints so end travellers only need the link.
export default function PublicTravelDiary() {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('trips');

  // Reroute the in-render getExperience calls inside @koob/trip-export to the
  // public endpoints. Set synchronously so it is active before any fetch.
  setPublicShareToken(shareToken);
  useEffect(() => {
    setPublicShareToken(shareToken);
    return () => setPublicShareToken(null);
  }, [shareToken]);

  const [experiences, setExperiences] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [productsReady, setProductsReady] = useState(false);
  const [accommodationLabels, setAccommodationLabels] = useState();

  // Stable identity: react-async reloads whenever promiseFn changes. The
  // promise is kept per locale so the loader below and TripExport, which both
  // read the dictionary, share one request.
  const getTravelDiarySettings = useMemo(() => {
    const fetchSettings = getSharedTravelDiarySettings(shareToken);
    const requests = new Map();

    return ({ filters, skip } = {}) => {
      if (skip) {
        return Promise.resolve(null);
      }

      const locale = filters?.locale ?? '';
      if (!requests.has(locale)) {
        const request = fetchSettings({ filters });
        request.catch(() => requests.delete(locale));
        requests.set(locale, request);
      }
      return requests.get(locale);
    };
  }, [shareToken]);
  const getPricingSummary = useMemo(
    () => getSharedPricingSummary(shareToken),
    [shareToken],
  );
  const getOrganizationTheme = useMemo(
    () => getSharedOrganizationTheme(shareToken),
    [shareToken],
  );

  const {
    data: diaryData,
    isLoading: isLoadingDiary,
    isSettled: isDiarySettled,
  } = useAsync({
    promiseFn: getSharedTravelDiary,
    shareToken,
  });

  const { data: pricingData } = useAsync({
    promiseFn: getPricingSummary,
    watch: shareToken,
  });
  const trip = diaryData?.data?.trip;
  const tourOperator = diaryData?.data?.tourOperator;
  const countries = trip?.countries;

  // KD-3755: the diary opens in the locale the TO picked when copying the link
  // (?locale=). It lives in the link rather than on the trip, so each TO sharing
  // the same DMC template keeps its own. Never read from the browser: a first
  // visit, a revisit and a refresh all render the same language.
  const diaryLocale = searchParams.get('locale') || trip?.locale;
  const langSelected = useMemo(
    () =>
      diaryLocale
        ? { value: diaryLocale, label: diaryLocale.toUpperCase() }
        : null,
    [diaryLocale],
  );

  const { data: contentsData } = useAsync({
    promiseFn: getSharedTravelDiaryContents,
    shareToken,
    locale: diaryLocale,
    watch: `${shareToken}|${diaryLocale ?? ''}`,
  });

  const { data: settingsData, isRejected: isSettingsRejected } = useAsync({
    promiseFn: getTravelDiarySettings,
    filters: { locale: diaryLocale },
    skip: !diaryLocale,
    watch: diaryLocale,
  });
  const loadingSection = getSetting(settingsData?.data, 'loading-screen');
  const loaderTitle =
    getSettingLabel(loadingSection?.sections, 'loading-screen-title') ||
    t('export.publicLoading.title');
  const loaderDescription =
    getSettingLabel(loadingSection?.sections, 'loading-screen-description') ||
    t('export.publicLoading.description');
  // Hold the text back until the dictionary answers: a French diary must not
  // flash the English fallback first.
  const isLoaderTextReady = Boolean(settingsData) || isSettingsRejected;

  useEffect(() => {
    if (!trip || !diaryLocale) {
      return;
    }

    let cancelled = false;
    setProductsReady(false);

    const experienceIds = [
      ...new Set(trip.tripDays?.map(day => day.experienceId)?.filter(Boolean)),
    ];
    const hotelIds = [
      ...new Set(trip.tripDays?.map(day => day.hotelId)?.filter(Boolean)),
    ];

    // A single unfetchable product must never leave the diary stuck on its
    // loader: it is kept as an unavailable entry instead of rejecting the lot.
    const experiencePromises = experienceIds.map(async experienceId => {
      try {
        const experience = await getExperience({
          experienceId,
          locale: diaryLocale,
        });
        return experience.data;
      } catch {
        return { id: experienceId, isAccessible: false };
      }
    });

    const hotelPromises = hotelIds.map(async hotelId => {
      try {
        const hotel = await getSharedHotel({
          shareToken,
          hotelId,
          locale: diaryLocale,
        });
        return {
          ...hotel.data,
          id: decodeId(hotelId).toString(),
        };
      } catch {
        return { id: decodeId(hotelId).toString(), isAccessible: false };
      }
    });

    Promise.all([
      Promise.all(experiencePromises),
      Promise.all(hotelPromises),
    ]).then(([loadedExperiences, loadedHotels]) => {
      if (cancelled) {
        return;
      }
      setExperiences(loadedExperiences);
      setHotels(loadedHotels);
      setProductsReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [trip, diaryLocale, shareToken]);

  useEffect(() => {
    if (!diaryLocale) {
      return;
    }

    let cancelled = false;
    getSharedAccommodationLabels({ shareToken, locale: diaryLocale })
      .then(res => !cancelled && setAccommodationLabels(res?.data))
      .catch(() => !cancelled && setAccommodationLabels(undefined));

    return () => {
      cancelled = true;
    };
  }, [shareToken, diaryLocale]);

  const images = experiences?.reduce((acc, experience) => {
    if (experience.pictures) {
      experience.pictures.forEach(image => {
        acc.push(image);
      });
    }
    return acc;
  }, []);

  // `productsReady` rather than per-product loading flags: those were still
  // false for one render between the diary call and the products effect, which
  // mounted TripExport on an empty diary and tore it down again.
  const loading = isLoadingDiary || (Boolean(trip) && !productsReady);
  const isUnavailable = isDiarySettled && !trip;

  // Same cancelled-extras filtering as the authenticated export page.
  const { bookedExtraIds, activeExtraIds } = useMemo(() => {
    const bookedExtraIds = new Set();
    const activeExtraIds = new Set();
    trip?.bookings?.forEach(booking =>
      booking?.experiencesBooked?.forEach(experienceBooked =>
        experienceBooked?.extras?.forEach(extra => {
          bookedExtraIds.add(extra.id);
          if (extra.state !== AdminBookingStateEnum.CANCELLED) {
            activeExtraIds.add(extra.id);
          }
        }),
      ),
    );
    return { bookedExtraIds, activeExtraIds };
  }, [trip]);

  const filteredTrip = useMemo(() => {
    if (!trip) return trip;
    const isCancelledOption = option => {
      const optionId = option.optionId ?? option.id;
      return bookedExtraIds.has(optionId) && !activeExtraIds.has(optionId);
    };
    return {
      ...trip,
      globalOptions: trip.globalOptions?.filter(
        option => !isCancelledOption(option),
      ),
      tripDays: trip.tripDays?.map(day => ({
        ...day,
        options: day.options?.filter(option => !isCancelledOption(option)),
      })),
    };
  }, [trip, bookedExtraIds, activeExtraIds]);

  if (isUnavailable) {
    return (
      <KoobContainer width="narrow">
        <KoobEmptyState title={t('export.publicUnavailable')} />
      </KoobContainer>
    );
  }

  return (
    <>
      {loading ? (
        // No Koob branding on the traveller's screen: the TO logo once the
        // diary is known, no logo before that or when the TO has none.
        <KoobLoaderFullscreen
          hideLogo={!tourOperator?.avatarUrl}
          logoUrl={tourOperator?.avatarUrl}
          logoAlt={tourOperator?.displayName}
          title={isLoaderTextReady ? loaderTitle : undefined}
          description={isLoaderTextReady ? loaderDescription : undefined}
        />
      ) : (
        <TripExport
          trip={filteredTrip}
          countries={countries}
          tourOperator={tourOperator}
          images={images}
          experiences={experiences}
          hotels={hotels}
          accommodationLabels={accommodationLabels}
          getTravelDiarySettings={getTravelDiarySettings}
          getExperience={getExperience}
          langSelected={langSelected}
          setLangSelected={noop}
          showLanguageModal={false}
          setShowLanguageModal={noop}
          shareUrl={window.location.href}
          travelDiaryContents={contentsData?.data ?? []}
          pricing={pricingData?.data}
          getOrganizationTheme={getOrganizationTheme}
        />
      )}
    </>
  );
}
